import { useContext } from "react";
import { Formik, Form, FieldArray, Field } from "formik";
import Input from "components/Input";
import Dropdown from "components/Dropdown";
import ImageUpload from "components/ImageUpload";
import FormWrapper from "components/FormWrapper";
import ResetAndSubmit from "components/ResetAndSubmit";
import * as Yup from "yup";
import TextArea from "components/TextArea";
import { AppContext } from "state";
import { getImageValidationSchema } from "constants/validationSchemas";
import Card from "components/OnboardingFormCard";
import { generateUniqueId } from "utils";

const validationSchema = Yup.object({
  faculties: Yup.array().of(
    Yup.object().shape({
      FACULTY_NAME: Yup.string().required("Faculty name is Required"),
      FACULTY_DESIGNATION: Yup.string().required("Faculty designation is required"),
      FACULTY_EMAIL: Yup.string().required("email is required"),
      GENDER: Yup.string().required("gender is required"),
      COUNTRY_CODE: Yup.string().required("country code is required"),
      FACULTY_MOBILE: Yup.string().matches(/^\d{10}$/, 'Mobile number must be exactly 10 digits').required('Mobile number is required'),
      FACULTY_SUBJECT: Yup.array().min(1, "Atleast 1 Faculty subject is Required"),
      FACULTY_QUALIFICATION: Yup.string().required("Faculty qualification is Required"),
      FACULTY_EXPERIENCE: Yup.number().required("Faculty experience is Required"),
      FACULTY_IMAGE: getImageValidationSchema("Faculty image is Required"),
    })
  ),
});

const Faculties = ({ handleNavigate, resetFormData, disableForm }: { handleNavigate, resetFormData?: any, titleAndDesNotRequired?: boolean, disableForm?: boolean }) => {
  const { globalData } = useContext(AppContext);

  const createEmptyFaculty = () => ({
    ID: generateUniqueId(),
    DRAWER: false,
    GENDER: "",
    FACULTY_EMAIL: "",
    COUNTRY_CODE: "+91",
    FACULTY_NAME: "",
    FACULTY_MOBILE: "",
    FACULTY_DESIGNATION: "",
    FACULTY_SUBJECT: [],
    FACULTY_QUALIFICATION: "",
    FACULTY_EXPERIENCE: "",
    FACULTY_DESCRIPTION: "",
    FACULTY_IMAGE: "",
  });

  const loadInitialData = () => {
    const savedData = JSON.parse(sessionStorage.getItem("faculties")) || [];
    return savedData.length ? savedData : [createEmptyFaculty()];
  };

  return (
    <Formik
      initialValues={{ faculties: loadInitialData() }}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={(values) => {
        console.log("values.faculties ", values.faculties);
        sessionStorage.setItem("faculties", JSON.stringify(values.faculties));
        handleNavigate();
      }}
    >
      {({ values, handleSubmit, resetForm, errors, setFieldValue }) => (
        <FormWrapper
          title={"Let’s show our heroes!"}
          description={"Upload your teachers' photos and showcase the inspiring faces behind the learning. Highlight the educators shaping your students' future!"}
          addMore={
            !!values.faculties?.[0]?.FACULTY_NAME
              ? () =>
                  setFieldValue("faculties", [
                    ...(values.faculties || []),
                    {
                      ...createEmptyFaculty(),
                      DRAWER: true,
                    },
                  ])
              : ""
          }
          addMoreLabel={"Add Faculty Member"}
          childern={
            <Form onSubmit={handleSubmit}>
              <div>
                <div className="rounded-2xl my-1 mx-1">
                  <FieldArray name="faculties">
                    {({ push, remove, form }) => (
                      <div className="grid grid-cols-4 gap-4 sm:grid-cols-1 md:grid-cols-1">
                        {values.faculties.map((faculty, index) => (
                          <Card
                            key={faculty?.ID}
                            isError={Boolean(errors?.faculties?.[index])}
                            index={index}
                            handleOpenDrawer={() => form.setFieldValue(`faculties.${index}.DRAWER`, true)}
                            handleCloseDrawer={() => form.setFieldValue(`faculties.${index}.DRAWER`, false)}
                            onDelete={() => {
                              if (values.faculties?.length > 1) {
                                remove(index);
                              } else {
                                remove(index);
                                push({ ...createEmptyFaculty(), DRAWER: false });
                              }
                            }}
                            element={faculty}
                            children={
                              <div className="flex flex-wrap gap-x-2 gap-y-4">
                                <div className="w-[48%]">
                                  <Input
                                    disable={disableForm}
                                    label="Name"
                                    name={`faculties.${index}.FACULTY_NAME`}
                                    type="text"
                                    placeHolder="Enter name"
                                    required
                                  />
                                </div>
                                <div className="w-[48%]">
                                  <Dropdown
                                    disable={disableForm}
                                    label="Subject"
                                    name={`faculties.${index}.FACULTY_SUBJECT`}
                                    placeHolder="Select Subjects"
                                    options={globalData?.SubjectOptions}
                                    customHeight="h-auto"
                                    isMulti
                                    required
                                  />
                                </div>
                                <div className="w-[48%]">
                                  <Input
                                    disable={disableForm}
                                    label="Mobile"
                                    name={`faculties.${index}.FACULTY_MOBILE`}
                                    type="text"
                                    placeHolder="Enter mobile"
                                    isMobile
                                    countryCodeName={`faculties.${index}.COUNTRY_CODE`}
                                    required
                                  />
                                </div>
                                <div className="w-[48%]">
                                  <Input
                                    disable={disableForm}
                                    label="Email"
                                    name={`faculties.${index}.FACULTY_EMAIL`}
                                    type="email"
                                    placeHolder="Enter email"
                                    required
                                  />
                                </div>
                                <div className="w-[48%]">
                                  <Input
                                    disable={disableForm}
                                    label="Designation"
                                    name={`faculties.${index}.FACULTY_DESIGNATION`}
                                    type="text"
                                    placeHolder="Enter Designation"
                                    required
                                  />
                                </div>
                                <div className="w-[48%]">
                                  <Input
                                    disable={disableForm}
                                    label="Qualification"
                                    name={`faculties.${index}.FACULTY_QUALIFICATION`}
                                    type="text"
                                    placeHolder="Enter Qualification"
                                    required
                                  />
                                </div>
                                <div className="w-[48%]">
                                  <Dropdown
                                    disable={disableForm}
                                    name={`faculties.${index}.GENDER`}
                                    label="Gender"
                                    placeHolder="Select Gender"
                                    required
                                    options={[
                                      { value: "MALE", label: "MALE" },
                                      { value: "FEMALE", label: "FEMALE" },
                                      { value: "OTHER", label: "OTHER" },
                                    ]}
                                  />
                                </div>
                                <div className="w-[48%]">
                                  <Input
                                    disable={disableForm}
                                    label="Experience"
                                    name={`faculties.${index}.FACULTY_EXPERIENCE`}
                                    type="number"
                                    placeHolder="Enter years of experience"
                                    required
                                  />
                                </div>
                                <div className="w-full">
                                  <TextArea
                                    disabled={
                                      disableForm ||
                                      !(
                                        !!faculty?.FACULTY_NAME &&
                                        !!faculty?.FACULTY_DESIGNATION &&
                                        !!String(faculty?.FACULTY_SUBJECT) &&
                                        !!faculty?.FACULTY_EXPERIENCE &&
                                        !!faculty?.FACULTY_QUALIFICATION
                                      )
                                    }
                                    label="Description"
                                    name={`faculties.${index}.FACULTY_DESCRIPTION`}
                                    autoGenerate={true}
                                    generate={{
                                      category: "faculty",
                                      kwargs: {
                                        name: faculty?.FACULTY_NAME,
                                        designation: faculty?.FACULTY_DESIGNATION,
                                        subject: String(faculty?.FACULTY_SUBJECT),
                                        experience: faculty?.FACULTY_EXPERIENCE,
                                        qualification: faculty?.FACULTY_QUALIFICATION,
                                      },
                                    }}
                                  />
                                </div>
                                <div className="w-full">
                                  <Field name={`faculties.${index}.FACULTY_IMAGE`}>
                                    {({ field, form, meta }) => (
                                      <ImageUpload
                                        isError={!!meta.touched && !!meta.error}
                                        disable={disableForm}
                                        label="Upload Faculty Profile picture"
                                        maxSize={5}
                                        labelClassNames="text-[2vh]"
                                        containerHeight={{
                                          width: "7vh",
                                          height: "7vh",
                                        }}
                                        imageValue={field.value}
                                        onChange={(item) =>
                                          form.setFieldValue(`faculties.${index}.FACULTY_IMAGE`, item)
                                        }
                                      />
                                    )}
                                  </Field>
                                </div>
                              </div>
                            }
                          />
                        ))}
                      </div>
                    )}
                  </FieldArray>
                </div>
              </div>
              {!disableForm && (
                <ResetAndSubmit
                  submitToolTip={
                    !Boolean(values?.faculties?.filter((item) => !!item?.FACULTY_NAME)?.length)
                      ? "At least one data entry is required for the next step."
                      : Boolean(errors?.faculties)
                      ? "Please fill in all the required fields."
                      : ""
                  }
                  isError={
                    !Boolean(values?.faculties?.filter((item) => !!item?.FACULTY_NAME)?.length) ||
                    Boolean(errors?.faculties)
                  }
                  resetForm={() => {
                    resetForm();
                    resetFormData();
                  }}
                />
              )}
            </Form>
          }
        />
      )}
    </Formik>
  );
};

export default Faculties;
